// =============================================
// DomainExpirySync — przycisk "Sprawdź daty w AfterMarket" + raport
//
// Koniec z ręcznym wpisywaniem dat wygaśnięcia. Jedno kliknięcie pobiera
// konto AfterMarket i przepisuje daty do panelu.
//
// Dwa konta AfterMarket (od 2026-09-22 oba obsługiwane):
//   🏢 Firma      → klucze *_FIRMA (albo legacy) w Coolify
//   🎨 Sponsoraki → klucze *_SPONSORAKI w Coolify
// Przycisk podąża za filtrem workspace w tabeli: firma → sync konta firmowego,
// sideprojects → sync konta Sponsoraków, „Wszystkie" → oba po kolei, każde
// z osobnym raportem. Filtr „tylko wiersze z tego workspace" siedzi w
// aftermarketService (isSyncable + buildSyncPlan), nie tutaj.
// =============================================
import React, { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { CalendarClock, Loader2, ArrowRight, CheckCircle2, AlertTriangle, Info, XCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import type { Website } from "@/hooks/useWebsites";
import {
  syncDomainExpiry,
  isSyncable,
  WORKSPACE_LABEL,
  type AmWorkspace,
  type DomainSyncReport,
} from "@/services/aftermarketService";

interface Props {
  websites: Website[];
  /** Konta do sprawdzenia, w kolejności. Puste = przycisk nieaktywny. */
  workspaces: AmWorkspace[];
  /** Odświeżenie listy po zapisie (sync pisze bezpośrednio do bazy). */
  onSynced: () => void;
}

const WS_ICON: Record<AmWorkspace, string> = { firma: "🏢", sideprojects: "🎨" };

/** Wynik dla jednego konta — raport albo błąd (np. brak klucza _SPONSORAKI w Coolify). */
type AccountResult = { workspace: AmWorkspace; report: DomainSyncReport } | { workspace: AmWorkspace; error: string };

const DomainExpirySync: React.FC<Props> = ({ websites, workspaces, onSynced }) => {
  const { toast } = useToast();
  const [running, setRunning] = useState(false);
  const [phase, setPhase] = useState<string>("");
  const [results, setResults] = useState<AccountResult[] | null>(null);

  // Ile wierszy faktycznie podlega sprawdzeniu dla wybranych kont.
  const counts = useMemo(() => {
    const per: Record<AmWorkspace, number> = { firma: 0, sideprojects: 0 };
    for (const w of websites) {
      if (isSyncable(w, "firma")) per.firma++;
      else if (isSyncable(w, "sideprojects")) per.sideprojects++;
    }
    const total = workspaces.reduce((acc, ws) => acc + per[ws], 0);
    return { per, total };
  }, [websites, workspaces]);

  const label =
    workspaces.length === 1
      ? `${WS_ICON[workspaces[0]]} ${WORKSPACE_LABEL[workspaces[0]]}`
      : workspaces.map((ws) => WS_ICON[ws]).join(" ") + " oba konta";

  const handleSync = async () => {
    if (running || counts.total === 0 || workspaces.length === 0) return;
    setRunning(true);
    const out: AccountResult[] = [];
    let anySaved = false;

    // Konta po kolei — każde ma własny klucz w nginx (X-Workspace) i własny
    // raport. Błąd jednego konta NIE przerywa drugiego.
    for (const ws of workspaces) {
      if (counts.per[ws] === 0) continue;
      const wsLabel = `${WS_ICON[ws]} ${WORKSPACE_LABEL[ws]}`;
      setPhase(`${wsLabel}: pobieram listę…`);
      try {
        const report = await syncDomainExpiry(websites, ws, (p, done, total) => {
          setPhase(p === "fetch" ? `${wsLabel}: pobrano ${done} domen…` : `${wsLabel}: zapisuję ${done}/${total}…`);
        });
        out.push({ workspace: ws, report });
        anySaved = true;
      } catch (err) {
        out.push({ workspace: ws, error: err instanceof Error ? err.message : String(err) });
      }
    }

    setRunning(false);
    setPhase("");
    if (anySaved) onSynced();

    if (out.length === 1 && "error" in out[0]) {
      // Jedno konto, jeden błąd — toast wystarczy, dialog byłby pusty.
      toast({
        title: `Sprawdzanie dat (${WORKSPACE_LABEL[out[0].workspace]}) nie powiodło się`,
        description: out[0].error,
        variant: "destructive",
      });
      return;
    }
    setResults(out);
  };

  const title =
    workspaces.length === 0
      ? "Wybierz workspace w filtrze, żeby sprawdzić daty."
      : `Pobiera realne daty wygaśnięcia z konta AfterMarket (${label}) i nadpisuje je w panelu. ` +
        `Sprawdzane: ${workspaces.map((ws) => `${WORKSPACE_LABEL[ws]} ${counts.per[ws]}`).join(", ")}.`;

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={handleSync}
        disabled={running || counts.total === 0 || workspaces.length === 0}
        title={title}
      >
        {running ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            {phase || "Sprawdzam…"}
          </>
        ) : (
          <>
            <CalendarClock className="h-4 w-4 mr-2" />
            Sprawdź daty w AfterMarket · {label} ({counts.total})
          </>
        )}
      </Button>

      <Dialog open={!!results} onOpenChange={(open) => !open && setResults(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Wynik sprawdzania dat</DialogTitle>
            <DialogDescription>
              {results && results.length > 1
                ? "Każde konto AfterMarket sprawdzone osobno — poniżej raport per konto."
                : results && "report" in results[0]
                  ? `Konto ${WORKSPACE_LABEL[results[0].workspace]} zwróciło ${results[0].report.accountTotal} domen.`
                  : null}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {results?.map((r) => (
              <div key={r.workspace} className="space-y-4">
                {results.length > 1 && (
                  <h3 className="text-sm font-semibold flex items-center gap-2">
                    <span>{WS_ICON[r.workspace]}</span>
                    {WORKSPACE_LABEL[r.workspace]}
                    {"report" in r && (
                      <span className="text-xs font-normal text-muted-foreground">— konto zwróciło {r.report.accountTotal} domen</span>
                    )}
                  </h3>
                )}
                {"error" in r ? (
                  <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-xs flex gap-2">
                    <XCircle className="h-4 w-4 text-red-400 shrink-0 mt-0.5" />
                    <span>{r.error}</span>
                  </div>
                ) : (
                  <ReportBody report={r.report} />
                )}
              </div>
            ))}
          </div>

          <DialogFooter>
            <Button onClick={() => setResults(null)}>Zamknij</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

const ReportBody: React.FC<{ report: DomainSyncReport }> = ({ report }) => (
  <div className="space-y-5">
    <div className="grid grid-cols-3 gap-3">
      <StatBox label="Zaktualizowane" value={report.updated.length} tone="ok" />
      <StatBox label="Bez zmian" value={report.unchanged.length} tone="muted" />
      <StatBox label="Brak na koncie" value={report.notOnAccount.length} tone="warn" />
    </div>

    {report.updated.length > 0 && (
      <Section icon={<CheckCircle2 className="h-4 w-4 text-emerald-400" />} title={`Zmienione daty (${report.updated.length})`}>
        {report.updated.map((c) => (
          <div key={c.id} className="flex items-center justify-between gap-3 text-xs py-1.5 border-b border-border/40 last:border-0">
            <span className="font-medium truncate">{c.name}</span>
            <span className="flex items-center gap-2 shrink-0 font-mono text-muted-foreground">
              {c.oldDate && c.oldDate !== "EMPTY" ? c.oldDate : "—"}
              <ArrowRight className="h-3 w-3" />
              <span className="text-emerald-400">{c.newDate || "—"}</span>
            </span>
          </div>
        ))}
      </Section>
    )}

    {report.notOnAccount.length > 0 && (
      <Section
        icon={<AlertTriangle className="h-4 w-4 text-amber-400" />}
        title={`Nie ma ich na koncie AfterMarket (${report.notOnAccount.length})`}
        hint="Przepadły albo są u innego rejestratora. Poprzednia data została nietknięta — sprawdź ręcznie."
      >
        {report.notOnAccount.map((c) => (
          <div key={c.id} className="flex items-center justify-between gap-3 text-xs py-1.5 border-b border-border/40 last:border-0">
            <span className="font-medium truncate">{c.name}</span>
            <span className="font-mono text-muted-foreground shrink-0">{c.oldDate && c.oldDate !== "EMPTY" ? c.oldDate : "brak daty"}</span>
          </div>
        ))}
      </Section>
    )}

    {report.extraOnAccount.length > 0 && (
      <Section
        icon={<Info className="h-4 w-4 text-blue-400" />}
        title={`Na koncie, ale nie ma ich w panelu (${report.extraOnAccount.length})`}
        hint="Tylko do wiadomości — nic nie zapisano."
      >
        <div className="flex flex-wrap gap-1.5 pt-1">
          {report.extraOnAccount.map((d) => (
            <Badge key={d} variant="outline" className="text-xs font-mono px-2 py-0.5">
              {d}
            </Badge>
          ))}
        </div>
      </Section>
    )}
  </div>
);

const StatBox: React.FC<{ label: string; value: number; tone: "ok" | "muted" | "warn" }> = ({ label, value, tone }) => (
  <div
    className={
      "rounded-lg border p-3 " +
      (tone === "ok"
        ? "border-emerald-500/30 bg-emerald-500/10"
        : tone === "warn"
          ? "border-amber-500/30 bg-amber-500/10"
          : "border-border bg-muted/20")
    }
  >
    <div className="text-2xl font-semibold">{value}</div>
    <div className="text-xs text-muted-foreground mt-0.5">{label}</div>
  </div>
);

const Section: React.FC<{ icon: React.ReactNode; title: string; hint?: string; children: React.ReactNode }> = ({
  icon,
  title,
  hint,
  children,
}) => (
  <div className="rounded-lg border p-4">
    <div className="flex items-center gap-2 mb-1">
      {icon}
      <h4 className="text-sm font-semibold">{title}</h4>
    </div>
    {hint && <p className="text-xs text-muted-foreground mb-3">{hint}</p>}
    <div className="max-h-64 overflow-y-auto">{children}</div>
  </div>
);

export default DomainExpirySync;
